/**
 * MUI 主题（U2 §2 / §5.1 / §A1 —— 深色未来感设计系统 + T01 明暗双主题）
 *
 *  - 默认 light（PRD T1），通过 ThemeModeProvider 切换；palette 由 createAppTheme(mode) 构造。
 *  - 设计 token 单一真源：颜色只在 palette 定义；tailwind 经 var(--zh-*) 映射（index.css）。
 *  - 组件一律走 sx palette 或 .glass 类，**禁止硬编码色值**。
 */
import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import type { ThemeMode } from './constants/theme';

const FONTS = '"PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif';

// 设计 token（单一真源，见 PRD §5.1）
export const designTokens = {
  bg: {
    base: '#0a0e1a', // 背景·根
    surface: '#121829', // 背景·面板
    elevated: '#1a2236', // 背景·悬浮
    glass: 'rgba(255,255,255,0.04)', // 背景·玻璃
  },
  primary: '#22d3ee', // 强调·霓虹青
  secondary: '#a855f7', // 强调·霓虹紫
  brand: '#34d399', // 强调·品牌绿（知会 AI 身份/成功态）
  text: {
    primary: '#e6edf3',
    secondary: '#9aa7bd',
    disabled: '#5b6678',
    onAccent: '#04121a', // 霓虹按钮上的深色字
  },
  border: {
    subtle: 'rgba(255,255,255,0.08)',
    strong: 'rgba(255,255,255,0.16)',
  },
  status: {
    success: '#34d399',
    warning: '#fbbf24',
    error: '#f87171',
    info: '#22d3ee',
  },
};

// 扩展 MUI Palette 以承载自定义 token（供 sx 使用，避免硬编码）
declare module '@mui/material/styles' {
  interface Palette {
    brand: string;
    onAccent: string;
    glass: string;
    bg: { base: string; surface: string; elevated: string; glass: string };
  }
  interface PaletteOptions {
    brand?: string;
    onAccent?: string;
    glass?: string;
    bg?: { base?: string; surface?: string; elevated?: string; glass?: string };
  }
}

/** 组件覆写（单一真源，light/dark 共用） */
export const muiComponents: ThemeOptions['components'] = {
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: 10,
        minHeight: 44,
        touchAction: 'manipulation',
        transition: 'box-shadow 120ms ease, background 120ms ease, transform 120ms ease',
        '&.Mui-focusVisible': {
          outline: `3px solid ${designTokens.primary}`,
          outlineOffset: 2,
        },
      },
      containedPrimary: {
        color: designTokens.text.onAccent,
        '&:hover': {
          boxShadow: `0 0 0 1px ${designTokens.primary}, 0 0 18px rgba(34,211,238,0.45)`,
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        minWidth: 44,
        minHeight: 44,
        touchAction: 'manipulation',
        '&.Mui-focusVisible': {
          outline: `3px solid ${designTokens.primary}`,
          outlineOffset: 2,
        },
      },
    },
  },
  MuiAppBar: {
    defaultProps: { elevation: 0 },
    styleOverrides: { root: { borderBottom: `1px solid ${designTokens.border.subtle}` } },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
      rounded: { borderRadius: 16 },
    },
  },
  MuiChip: { styleOverrides: { root: { borderRadius: 8 } } },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        background: designTokens.bg.elevated,
        color: designTokens.text.primary,
        border: `1px solid ${designTokens.border.subtle}`,
        fontSize: 12,
      },
    },
  },
};

/**
 * 依据 mode 构造 MUI 主题。
 *  - dark：沿用现有霓虹未来风（designTokens）。
 *  - light：同色系强调色反转背景/文字（glass/霓虹描边降透明度，避免浅底刺眼）。
 */
export function createAppTheme(mode: ThemeMode): Theme {
  const dark = mode === 'dark';
  const palette: ThemeOptions['palette'] = {
    mode,
    primary: { main: dark ? designTokens.primary : '#0891b2' },
    secondary: { main: dark ? designTokens.secondary : '#7c3aed' },
    success: { main: dark ? designTokens.status.success : '#059669' },
    warning: { main: dark ? designTokens.status.warning : '#d97706' },
    error: { main: dark ? designTokens.status.error : '#dc2626' },
    info: { main: dark ? designTokens.status.info : '#0891b2' },
    background: dark
      ? { default: designTokens.bg.base, paper: designTokens.bg.surface }
      : { default: '#f4f6fb', paper: '#ffffff' },
    text: dark
      ? {
          primary: designTokens.text.primary,
          secondary: designTokens.text.secondary,
          disabled: designTokens.text.disabled,
        }
      : { primary: '#0f172a', secondary: '#5b6678', disabled: '#9aa7bd' },
    divider: dark ? designTokens.border.subtle : 'rgba(15,23,42,0.08)',
    brand: dark ? designTokens.brand : '#059669',
    onAccent: designTokens.text.onAccent,
    glass: dark ? designTokens.bg.glass : 'rgba(255,255,255,0.65)',
    bg: dark
      ? designTokens.bg
      : {
          base: '#f4f6fb',
          surface: '#ffffff',
          elevated: '#eef2f9',
          glass: 'rgba(255,255,255,0.65)',
        },
  };

  return createTheme({
    palette,
    typography: {
      fontFamily: FONTS,
      fontSize: 14,
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    shape: { borderRadius: 12 },
    components: muiComponents,
  } satisfies ThemeOptions);
}

/** 兼容导出：默认 dark（仅向后兼容，运行时由 ThemeModeProvider 接管） */
const theme = createAppTheme('dark');
export default theme;
